import ImageUpload from "@/components/ImageUpload";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ColorPicker } from "@/components/ui/colour-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useOrgEntitlements } from "@/lib/hooks/useOrgEntitlements";
import { applyOrgDtoToSession } from "@/lib/org/applyOrgDtoToSession";
import { requirePermission } from "@/lib/permissions/permissions";
import { useHasPermission } from "@/lib/states/session/hooks";
import { store } from "@/lib/states/store";
import { supabase } from "@/lib/supabase/supabaseClient";
import { trpc } from "@/lib/trpc/client";
import { trpcUtils } from "@/lib/trpc/vanilla";
import { errorToast } from "@/lib/utils/ToastUtils";
import {
  canUseBranding,
  canUsePublicIdentifier,
  PLAN_LABELS,
} from "@/lib/billing/plans";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Carrot,
  CreditCard,
  Dumbbell,
  House,
  LayoutTemplate,
  Mail,
  MessageCircle,
  Smartphone,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_auth/_dashboard/branding")({
  beforeLoad: () => {
    requirePermission("organisation.manage");
  },
  component: BrandingPage,
  loader: async () => {
    const org = store.getState().session.org;
    if (!org) {
      throw new Error("Org not found");
    }

    const profile = await trpcUtils.org.get.ensureData();
    return { org, imageUrl: profile.logo?.url ?? "" };
  },
});

function BrandingPage() {
  const { org, imageUrl } = Route.useLoaderData();
  const canManageOrg = useHasPermission("organisation.manage");
  const { data: entitlements } = useOrgEntitlements();
  const allowBranding = canUseBranding(entitlements);
  const allowPublicIdentifier = canUsePublicIdentifier(entitlements);

  const [image, setImage] = useState<string>(imageUrl);
  const [imageFile, setImageFile] = useState<File>();
  const [loading, setLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [orgSlug, setOrgSlug] = useState(org.slug ?? "");
  const [primaryColor, setPrimaryColor] = useState(org.primaryColor ?? "");
  const [logoVersion, setLogoVersion] = useState(org.logoVersion ?? 0);
  const [slugError, setSlugError] = useState<string | null>(null);
  const updateProfile = trpc.org.updateProfile.useMutation();
  const createLogoUpload = trpc.org.createLogoUpload.useMutation();
  const utils = trpc.useUtils();

  const maxLogoSizeBytes = 2 * 1024 * 1024;
  const logoPath = org.logoPath ?? `${org.id}/logo.png`;
  const normalizedPrimary = primaryColor.trim();
  const initialSlug = org.slug ?? "";
  const initialPrimaryColor = (org.primaryColor ?? "").trim();
  const normalizedSlug = normalizeSlug(orgSlug);
  const previewBrandColor = isHexColor(normalizedPrimary)
    ? normalizedPrimary
    : isHexColor(initialPrimaryColor)
      ? initialPrimaryColor
      : "#0051FF";
  const previewAccent = mixHex(previewBrandColor, "#ffffff", 0.88);
  const previewSlug = normalizedSlug || initialSlug || "my-coaching";
  const hasChanges =
    (allowPublicIdentifier && normalizedSlug !== initialSlug) ||
    (allowBranding && normalizedPrimary !== initialPrimaryColor) ||
    !!imageFile;

  const onImageChanged = (file: File) => {
    if (file.size > maxLogoSizeBytes) {
      toast.error("Logo must be 2MB or smaller");
      return;
    }
    setImage(URL.createObjectURL(file));
    setImageFile(file);
  };

  const handleSaveSettings = async () => {
    if (allowBranding && normalizedPrimary && !isHexColor(normalizedPrimary)) {
      toast.error("Primary colour must be a 6 digit hex value");
      return;
    }
    if (
      allowPublicIdentifier &&
      normalizedSlug &&
      !/^[a-z0-9-_]+$/.test(normalizedSlug)
    ) {
      toast.error(
        "Public identifier can only include lowercase letters, numbers, hyphens, and underscores",
      );
      return;
    }
    if (!hasChanges) {
      return;
    }

    setSlugError(null);
    if (
      allowPublicIdentifier &&
      normalizedSlug &&
      normalizedSlug !== initialSlug
    ) {
      try {
        const { available } = await trpcUtils.org.checkSlugAvailable.ensureData(
          { slug: normalizedSlug },
        );
        if (!available) {
          const message = "That public identifier is already in use.";
          setSlugError(message);
          toast.error(message, {
            description:
              "Try a different identifier so your public links stay unique.",
          });
          return;
        }
      } catch (error) {
        errorToast(error as Error);
        return;
      }
    }

    setSettingsSaving(true);

    try {
      let nextLogoVersion = logoVersion;
      let nextLogoPath = org.logoPath ?? logoPath;

      if (imageFile) {
        setLoading(true);
        const signed = await createLogoUpload.mutateAsync();
        const { error: uploadError } = await supabase.storage
          .from(signed.bucket)
          .uploadToSignedUrl(signed.path, signed.token, imageFile);

        if (uploadError) {
          errorToast(uploadError);
          return;
        }

        nextLogoVersion = logoVersion + 1;
        nextLogoPath = signed.path;
      }

      const updated = await updateProfile.mutateAsync({
        ...(allowBranding
          ? { primaryColor: normalizedPrimary || null }
          : {}),
        ...(allowPublicIdentifier
          ? { slug: normalizedSlug || null }
          : {}),
        ...(imageFile
          ? { logoPath: nextLogoPath, logoVersion: nextLogoVersion }
          : {}),
      });

      applyOrgDtoToSession(updated);
      utils.org.get.setData(undefined, () => updated);

      if (imageFile) {
        setLogoVersion(updated.logo_version);
        setImage(updated.logo?.url ?? image);
        setImageFile(undefined);
      }

      toast.success("Branding updated");
    } catch (error) {
      if (isConflictError(error)) {
        const message = "That public identifier is already in use.";
        setSlugError(message);
        toast.error(message, {
          description:
            "Try a different identifier so your public links stay unique.",
        });
        return;
      }
      errorToast(error as Error);
    } finally {
      setLoading(false);
      setSettingsSaving(false);
    }
  };

  if (!canManageOrg) {
    return null;
  }

  return (
    <div className="min-h-full ">
      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <Card className="overflow-hidden border-border/80 bg-card/95 xl:sticky xl:top-6">
            <CardHeader className="border-b border-border/80 bg-muted/30">
              <CardTitle className="text-xl text-foreground">
                Branding
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="space-y-2">
                <Label>Logo</Label>
                <ImageUpload
                  image={image}
                  onImageFileChanged={onImageChanged}
                  imageClassName="max-w-[72px] rounded-lg border border-border bg-background p-0.5 shadow-sm"
                  emptyView={
                    <div className="flex h-28 w-full items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 px-4 text-center text-sm font-medium text-muted-foreground">
                      Upload logo
                    </div>
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Primary colour</Label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <ColorPicker
                    size="icon"
                    value={primaryColor || "#0051FF"}
                    onChange={(value) => setPrimaryColor(value as string)}
                    disabled={!allowBranding}
                  />
                  <Input
                    value={primaryColor}
                    onChange={(event) => setPrimaryColor(event.target.value)}
                    placeholder="#0051FF"
                    className="sm:max-w-[180px]"
                    disabled={!allowBranding}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    className="justify-start px-0 text-muted-foreground hover:text-foreground"
                    onClick={() => setPrimaryColor("")}
                    disabled={!primaryColor || !allowBranding}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="branding-public-identifier">
                  Public identifier
                </Label>
                <Input
                  id="branding-public-identifier"
                  value={orgSlug}
                  onChange={(event) => {
                    setSlugError(null);
                    setOrgSlug(event.target.value);
                  }}
                  onBlur={() => setOrgSlug(normalizeSlug(orgSlug))}
                  placeholder="my-coaching"
                  disabled={!allowPublicIdentifier}
                />
                <p className="text-xs leading-5 text-muted-foreground">
                  Used for public pages like forms and packages
                  <br />
                  <span className="inline-flex items-center rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-[11px] text-foreground">
                    forms.trainrr.app/
                    <span className="font-semibold">{previewSlug}</span>
                  </span>
                </p>
                {slugError ? (
                  <p className="text-xs text-destructive">{slugError}</p>
                ) : null}
              </div>

              {(!allowBranding || !allowPublicIdentifier) && (
                <Alert
                  variant="info"
                  className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/40"
                >
                  <Zap className="h-4 w-4" />
                  <AlertTitle>Upgrade to unlock full branding</AlertTitle>
                  <AlertDescription className="text-xs leading-5">
                    Upgrade to unlock custom public identifiers and brand
                    colours
                    {entitlements?.required_plan_for_client_limit
                      ? ` (${PLAN_LABELS[entitlements.required_plan_for_client_limit]})`
                      : ""}
                    .{" "}
                    <Link
                      to="/upgrade"
                      search={{ plan: "growth_monthly" }}
                      className="underline underline-offset-2"
                    >
                      View plans
                    </Link>
                  </AlertDescription>
                </Alert>
              )}

              <Separator />

              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving || loading || !hasChanges}
                >
                  Save
                  {(settingsSaving || loading) && <LoadingSpinner />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="app" className="min-w-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Preview
              </p>

              <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-2xl border border-border bg-muted/60 p-1">
                <TabsTrigger value="app" className="rounded-xl px-4 py-2">
                  <Smartphone className="mr-2 h-4 w-4" />
                  App
                </TabsTrigger>
                <TabsTrigger value="emails" className="rounded-xl px-4 py-2">
                  <Mail className="mr-2 h-4 w-4" />
                  Emails
                </TabsTrigger>
                <TabsTrigger
                  value="payment-form"
                  className="rounded-xl px-4 py-2"
                >
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  Payment &amp; Form
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="app" className="mt-0 pt-4">
              <AppPreview
                imageUrl={image}
                orgName={org.name}
                brandColor={previewBrandColor}
                accentColor={previewAccent}
              />
            </TabsContent>

            <TabsContent value="emails" className="mt-0 pt-4">
              <EmailPreview
                imageUrl={image}
                orgName={org.name}
                brandColor={previewBrandColor}
              />
            </TabsContent>

            <TabsContent value="payment-form" className="mt-0 pt-4">
              <CommercePreview
                imageUrl={image}
                orgName={org.name}
                slug={previewSlug}
                brandColor={previewBrandColor}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function AppPreview({
  imageUrl,
  orgName,
  brandColor,
  accentColor,
}: {
  imageUrl: string;
  orgName: string;
  brandColor: string;
  accentColor: string;
}) {
  const resolvedOrgName = orgName.trim() || "Trainrr";
  const previewDate = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(new Date());
  const phoneBackground = mixHex("#ffffff", accentColor, 0.18);
  const surfaceTint = hexToRgba(accentColor, 0.2);

  return (
    <div className="flex min-h-[640px] items-center justify-center py-4">
      <div className="mx-auto w-full max-w-[332px] rounded-[42px] border border-border/70 bg-[#e9edf3] p-2.5 shadow-[0_22px_60px_rgba(15,23,42,0.18)]">
        <div
          className="relative overflow-hidden rounded-[34px] border border-black/5"
          style={{ backgroundColor: phoneBackground }}
        >
          <div className="flex min-h-[700px] flex-col px-6 pb-6 pt-8">
            <div className="flex items-center gap-3">
              <BrandMark
                imageUrl={imageUrl}
                name={resolvedOrgName}
                color={brandColor}
                size="lg"
              />
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold text-[#171b24]">
                  {resolvedOrgName}
                </p>
                <div className="mt-1 flex items-center gap-2 text-[12px] text-[#6b7280]">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: brandColor }}
                  />
                  <span>{previewDate}</span>
                </div>
              </div>
            </div>

            <div className="mt-7 space-y-6">
              <section>
                <PhoneCard>
                  <p className="text-[12px] text-[#7a8190]">Current Phase</p>
                  <p className="mt-1 text-[15px] font-semibold text-[#202531]">
                    Phase 1 - Foundations
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#d8dde6]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: "38%", backgroundColor: brandColor }}
                      />
                    </div>
                    <p className="text-[12px] text-[#7a8190]">3/8 weeks</p>
                  </div>
                </PhoneCard>
              </section>

              <section>
                <p className="mb-3 text-[15px] font-semibold text-[#1f2937]">
                  Metrics
                </p>
                <PhoneCard>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#7b8492]">
                        <TrendingUp
                          className="h-3.5 w-3.5"
                          style={{ color: brandColor }}
                        />
                        <span>Weight</span>
                      </div>
                      <div className="mt-6 flex items-baseline gap-1.5">
                        <span className="text-[20px] font-bold leading-none text-[#1d2430]">
                          84
                        </span>
                        <span className="text-[14px] font-medium text-[#6f7785]">
                          kg
                        </span>
                      </div>
                    </div>
                    <div className="w-[118px]">
                      <p className="text-right text-[12px] font-semibold text-[#7b8492]">
                        Apr 11
                      </p>
                      <div className="mt-4">
                        <MiniTrendLine
                          color={brandColor}
                          lineColor="#d8dde6"
                          points={[20, 18, 16, 14, 6]}
                        />
                      </div>
                    </div>
                  </div>
                </PhoneCard>
              </section>

              <section>
                <p className="mb-3 text-[15px] font-semibold text-[#1f2937]">
                  Activity
                </p>
                <PhoneCard>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#7b8492]">
                        <TrendingUp
                          className="h-3.5 w-3.5"
                          style={{ color: brandColor }}
                        />
                        <span>Steps</span>
                      </div>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="text-[20px] font-bold leading-none text-[#1d2430]">
                          0
                        </span>
                        <span className="text-[14px] font-medium text-[#6f7785]">
                          / 11,000
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 w-[96px]">
                      <MiniBarTrend color={brandColor} />
                    </div>
                  </div>
                </PhoneCard>
              </section>
            </div>

            <div className="mt-auto pt-6">
              <div
                className="rounded-[28px] px-4 py-3 shadow-[0_8px_20px_rgba(17,24,39,0.06)]"
                style={{ backgroundColor: hexToRgba("#ffffff", 0.92) }}
              >
                <div className="grid grid-cols-4 items-center gap-3 text-[#202531]">
                  <PhoneNavIcon
                    icon={<House className="h-5 w-5" strokeWidth={2.2} />}
                    active
                    activeColor={brandColor}
                    backgroundColor={surfaceTint}
                  />
                  <PhoneNavIcon
                    icon={<Dumbbell className="h-5 w-5" strokeWidth={2.2} />}
                  />
                  <PhoneNavIcon
                    icon={<Carrot className="h-5 w-5" strokeWidth={2.2} />}
                  />
                  <PhoneNavIcon
                    icon={
                      <MessageCircle className="h-5 w-5" strokeWidth={2.2} />
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-[22px] border border-[#e7ebf0] bg-white px-4 py-4 shadow-[0_2px_6px_rgba(17,24,39,0.05)] ${className}`.trim()}
    >
      {children}
    </div>
  );
}

function MiniTrendLine({
  points,
  color,
  lineColor,
}: {
  points: number[];
  color: string;
  lineColor: string;
}) {
  const width = 112;
  const height = 34;
  const step = width / Math.max(1, points.length - 1);
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(1, max - min);
  const path = points
    .map((point, index) => {
      const x = Number((index * step).toFixed(2));
      const y = Number((((max - point) / range) * (height - 6) + 3).toFixed(2));
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke={lineColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={path}
      />
      {points.map((point, index) => {
        const x = Number((index * step).toFixed(2));
        const y = Number(
          (((max - point) / range) * (height - 6) + 3).toFixed(2),
        );
        const active = index === points.length - 1;

        return (
          <circle
            key={`${point}-${index}`}
            cx={x}
            cy={y}
            r={active ? 4.5 : 3.5}
            fill={active ? color : "#ffffff"}
            stroke={active ? color : lineColor}
            strokeWidth="2"
          />
        );
      })}
    </svg>
  );
}

function MiniBarTrend({ color }: { color: string }) {
  const bars = [0.28, 0.42, 0.36, 0.52, 0.68, 0.84];

  return (
    <div className="flex h-10 items-end justify-end gap-1">
      {bars.map((height, index) => (
        <div
          key={`${height}-${index}`}
          className="w-2 rounded-full"
          style={{
            height: `${Math.round(height * 100)}%`,
            backgroundColor:
              index === bars.length - 1
                ? color
                : hexToRgba("#cfd6df", index === 0 ? 0.4 : 0.9),
          }}
        />
      ))}
    </div>
  );
}

function PhoneNavIcon({
  icon,
  active = false,
  activeColor = "#0051FF",
  backgroundColor = "rgba(0, 81, 255, 0.08)",
}: {
  icon: React.ReactNode;
  active?: boolean;
  activeColor?: string;
  backgroundColor?: string;
}) {
  return (
    <div className="flex justify-center">
      <div
        className="flex h-11 w-14 items-center justify-center rounded-full"
        style={
          active
            ? {
                color: activeColor,
                backgroundColor,
              }
            : { color: "#202531" }
        }
      >
        {icon}
      </div>
    </div>
  );
}

function EmailPreview({
  imageUrl,
  orgName,
  brandColor,
}: {
  imageUrl: string;
  orgName: string;
  brandColor: string;
}) {
  const resolvedOrgName = orgName.trim() || "Trainrr";

  return (
    <div className="flex min-h-[640px] items-center justify-center py-4">
      <BrowserMock hideChrome>
        <div className="min-h-[560px] bg-[#f4f5f7] p-6 text-[#0f172a]">
          <div className="mx-auto max-w-[560px]">
            <p
              className="mb-3 text-[12px] uppercase tracking-[0.14em]"
              style={{ color: brandColor }}
            >
              {resolvedOrgName}
            </p>
            <div
              className="overflow-hidden rounded-[24px] border border-[#e5e7eb] bg-white p-7 shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
              style={{ borderTop: `4px solid ${brandColor}` }}
            >
              <div className="mb-2">
                <BrandMark
                  imageUrl={imageUrl}
                  name={resolvedOrgName}
                  color={brandColor}
                  size="lg"
                />
              </div>
              <h3 className="mt-4 text-[24px] font-semibold leading-[1.1] text-[#0f172a]">
                Your invitation to {resolvedOrgName}
              </h3>

              <div>
                <p className="mt-3 text-[15px] leading-7 text-[#475569]">
                  You have been invited to use Trainrr by your coach at{" "}
                  {resolvedOrgName}.
                </p>
                <p className="mt-3 text-[15px] leading-7 text-[#475569]">
                  To get started download the app in the App Store or Google
                  Play Store.
                </p>
              </div>

              <div className="mt-6 border-t border-[#e2e8f0] pt-4 text-sm text-[#64748b]">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em]">
                    From
                  </p>
                  <p className="mt-2 font-semibold text-[#0f172a]">
                    {resolvedOrgName}
                  </p>
                </div>
              </div>

              <div className="mt-5 text-xs leading-5 text-[#94a3b8]">
                You received this email because your coach invited you to
                Trainrr.
              </div>
            </div>
          </div>
        </div>
      </BrowserMock>
    </div>
  );
}

function CommercePreview({
  imageUrl,
  orgName,
  slug,
  brandColor,
}: {
  imageUrl: string;
  orgName: string;
  slug: string;
  brandColor: string;
}) {
  const resolvedOrgName = orgName.trim() || "Trainrr";

  return (
    <Tabs defaultValue="payment" className="space-y-4">
      <div className="flex justify-end">
        <TabsList className="h-auto gap-2 rounded-2xl border border-border bg-muted/60 p-1">
          <TabsTrigger value="payment" className="rounded-xl px-4 py-2">
            <CreditCard className="mr-2 h-4 w-4" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="lead-form" className="rounded-xl px-4 py-2">
            <LayoutTemplate className="mr-2 h-4 w-4" />
            Lead form
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="payment" className="mt-0">
        <div className="flex min-h-[640px] items-center justify-center py-4">
          <BrowserMock url={`trainrr.app/${slug}/package/foundations`}>
            <div className="min-h-[560px] bg-white px-10 py-12 text-[#0f172a]">
              <main className="mx-auto max-w-[760px]">
                <div className="flex flex-col items-center gap-5 text-center">
                  <BrandMark
                    imageUrl={imageUrl}
                    name={resolvedOrgName}
                    color={brandColor}
                    size="lg"
                  />
                  <div>
                    <p className="text-2xl font-semibold tracking-tight text-[#0f172a]">
                      {resolvedOrgName}
                    </p>
                  </div>
                </div>

                <div className="mt-10">
                  <div className="space-y-4 text-left">
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <p className="text-3xl font-semibold tracking-tight text-[#0f172a]">
                          Foundations Coaching
                        </p>
                        <p className="mt-3 max-w-2xl text-sm leading-7 text-[#52525b]">
                          A focused remote coaching package with weekly
                          programming, feedback, and accountability.
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-[#d4d4d8] text-[#52525b]"
                      >
                        12 weeks
                      </Badge>
                    </div>

                    <div className="flex flex-wrap items-end justify-between gap-4 border-y border-[#e5e7eb] py-6">
                      <div>
                        <p
                          className="text-4xl font-semibold"
                          style={{ color: brandColor }}
                        >
                          $249
                        </p>
                        <p className="mt-1 text-sm text-[#52525b]">per month</p>
                      </div>
                      <div
                        className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white"
                        style={{ backgroundColor: brandColor }}
                      >
                        Subscribe
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-8 pt-2 text-sm text-[#52525b]">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em]">
                          Public URL
                        </p>
                        <p className="mt-2 font-semibold text-[#0f172a]">
                          /{slug}/package/foundations
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.18em]">
                          Checkout
                        </p>
                        <p className="mt-2 font-semibold text-[#0f172a]">
                          Branded CTA
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </BrowserMock>
        </div>
      </TabsContent>

      <TabsContent value="lead-form" className="mt-0">
        <div className="flex min-h-[640px] items-center justify-center py-4">
          <BrowserMock url={`trainrr.app/forms/${slug}/consultation`}>
            <div className="min-h-[560px] bg-white px-10 py-12 text-[#0f172a]">
              <main className="mx-auto max-w-[760px]">
                <div className="flex items-center gap-4">
                  <BrandMark
                    imageUrl={imageUrl}
                    name={resolvedOrgName}
                    color={brandColor}
                    size="md"
                  />
                  <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-[#71717a]">
                      {resolvedOrgName}
                    </p>
                    <h2 className="text-2xl font-semibold text-[#0f172a]">
                      Consultation application
                    </h2>
                  </div>
                </div>

                <div className="mt-10 space-y-8">
                  <section>
                    <p className="text-sm font-semibold text-[#0f172a]">
                      What outcome are you chasing over the next 90 days?
                    </p>
                    <div className="mt-4 space-y-3">
                      {[
                        "Build strength",
                        "Drop body fat",
                        "Improve consistency",
                      ].map((option, index) => {
                        const isSelected = index === 1;

                        return (
                        <div
                          key={option}
                          className="flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition"
                          style={
                            isSelected
                              ? {
                                  borderColor: brandColor,
                                  backgroundColor: hexToRgba(brandColor, 0.05),
                                  color: "#0f172a",
                                }
                              : {
                                  borderColor: "#e5e7eb",
                                  backgroundColor: "#ffffff",
                                  color: "#0f172a",
                                }
                          }
                        >
                          <span
                            className="flex h-4 w-4 items-center justify-center rounded-full border"
                            style={{
                              borderColor: isSelected ? brandColor : "#d4d4d8",
                            }}
                          >
                            {isSelected ? (
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{ backgroundColor: brandColor }}
                              />
                            ) : null}
                          </span>
                          {option}
                        </div>
                        );
                      })}
                    </div>
                  </section>

                  <section>
                    <p className="text-sm font-semibold text-[#0f172a]">
                      Tell us a bit about your current training routine
                    </p>
                    <div className="mt-4 rounded-[24px] border border-[#e5e7eb] px-4 py-5 text-sm leading-7 text-[#52525b]">
                      I train three times per week, mostly general gym work, and
                      I want more structure and accountability.
                    </div>
                  </section>

                  <div
                    className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white"
                    style={{ backgroundColor: brandColor }}
                  >
                    Continue
                  </div>
                </div>
              </main>
            </div>
          </BrowserMock>
        </div>
      </TabsContent>
    </Tabs>
  );
}

function BrowserMock({
  url,
  hideChrome = false,
  children,
}: {
  url?: string;
  hideChrome?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full max-w-[980px] rounded-[28px] border border-border/70 bg-muted/60 p-2 shadow-lg">
      <div className="overflow-hidden rounded-[22px] bg-background">
        {!hideChrome ? (
          <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
            </div>
            <div className="ml-2 flex-1 rounded-full border border-border/70 bg-background px-4 py-2 text-center text-xs text-muted-foreground shadow-sm">
              {url}
            </div>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function BrandMark({
  imageUrl,
  name,
  color,
  size,
}: {
  imageUrl?: string;
  name: string;
  color: string;
  size: "md" | "lg";
}) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    if (imageUrl) {
      setImageFailed(false);
    }
  }, [imageUrl]);

  const className =
    size === "lg"
      ? "h-16 w-16 rounded-2xl text-xl"
      : "h-12 w-12 rounded-xl text-base";

  if (imageUrl && !imageFailed) {
    return (
      <div
        className={`${className} overflow-hidden bg-white/10 ring-1 ring-black/5`}
      >
        <img
          src={imageUrl}
          alt={`${name || "Organisation"} logo`}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={`${className} flex items-center justify-center font-semibold text-white`}
      style={{ backgroundColor: color }}
    >
      {getInitials(name)}
    </div>
  );
}

function getInitials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return "T";
  }

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

function normalizeSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .replace(/-+/g, "-");
}

function isConflictError(error: unknown) {
  if (!error || typeof error !== "object") {
    return false;
  }
  const data =
    "data" in error
      ? (error as { data?: { code?: string } }).data
      : undefined;
  const message =
    "message" in error ? String((error as { message?: string }).message) : "";
  return (
    data?.code === "CONFLICT" ||
    message.includes("already in use") ||
    message.includes("organisations_slug_key")
  );
}

function isHexColor(value: string) {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

function hexToRgba(hex: string, alpha: number) {
  const safeHex = isHexColor(hex) ? hex : "#0051FF";
  const r = Number.parseInt(safeHex.slice(1, 3), 16);
  const g = Number.parseInt(safeHex.slice(3, 5), 16);
  const b = Number.parseInt(safeHex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mixHex(firstHex: string, secondHex: string, ratio: number) {
  const start = isHexColor(firstHex) ? firstHex : "#0051FF";
  const end = isHexColor(secondHex) ? secondHex : "#ffffff";
  const weight = Math.min(1, Math.max(0, ratio));
  const startRgb = hexToRgb(start);
  const endRgb = hexToRgb(end);

  const mixed = {
    r: Math.round(startRgb.r * (1 - weight) + endRgb.r * weight),
    g: Math.round(startRgb.g * (1 - weight) + endRgb.g * weight),
    b: Math.round(startRgb.b * (1 - weight) + endRgb.b * weight),
  };

  return `#${toHex(mixed.r)}${toHex(mixed.g)}${toHex(mixed.b)}`;
}

function hexToRgb(hex: string) {
  return {
    r: Number.parseInt(hex.slice(1, 3), 16),
    g: Number.parseInt(hex.slice(3, 5), 16),
    b: Number.parseInt(hex.slice(5, 7), 16),
  };
}

function toHex(value: number) {
  return value.toString(16).padStart(2, "0");
}
